import { useState, useEffect } from "react";
import { Link, Routes, Route, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAuth, API, resolveImageUrl } from "@/App";
import { toast } from "sonner";
import { 
  Mountain, LayoutDashboard, BookOpen, Users, MessageSquare, 
  Settings, LogOut, Plus, Trash2, FileText, Video, 
  Brain, Mail, Package, ChevronRight, PenLine, Save, Pencil, ArrowUp, ArrowDown
} from "lucide-react";

const AdminSidebar = () => {
  const location = useLocation();
  const { logout, user } = useAuth();
  const navigate = useNavigate();

  const navItems = [
    { path: "/admin", icon: LayoutDashboard, label: "Dashboard" },
    { path: "/admin/site-content", icon: PenLine, label: "Edit Site" },
    { path: "/admin/content", icon: BookOpen, label: "Content" },
    { path: "/admin/testimonials", icon: Users, label: "Testimonials" },
    { path: "/admin/frameworks", icon: Brain, label: "AI Training" },
    { path: "/admin/contacts", icon: Mail, label: "Contacts" },
    { path: "/admin/packages", icon: Package, label: "Packages" },
  ];

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  return (
    <div className="admin-sidebar w-64 min-h-screen p-6 flex flex-col">
      <Link to="/" className="flex items-center gap-2 mb-10">
        <Mountain className="h-8 w-8 text-orange-500" />
        <span className="font-serif text-xl font-medium text-white">Admin</span>
      </Link>

      <nav className="flex-1 space-y-2">
        {navItems.map((item) => {
          const isActive = location.pathname === item.path;
          return (
            <Link
              key={item.path}
              to={item.path}
              className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                isActive 
                  ? "bg-white/10 text-white" 
                  : "text-white/60 hover:bg-white/5 hover:text-white"
              }`}
              data-testid={`admin-nav-${item.label.toLowerCase()}`}
            >
              <item.icon className="h-5 w-5" />
              {item.label}
            </Link>
          );
        })}
      </nav>

      <div className="pt-6 border-t border-white/10">
        <div className="text-sm text-white/60 mb-4">
          Logged in as<br />
          <span className="text-white font-medium">{user?.email}</span>
        </div>
        <Button 
          variant="ghost" 
          className="w-full justify-start text-white/60 hover:text-white hover:bg-white/5"
          onClick={handleLogout}
          data-testid="admin-logout"
        >
          <LogOut className="h-4 w-4 mr-2" />
          Logout
        </Button>
      </div>
    </div>
  );
};

const DashboardHome = () => {
  const [stats, setStats] = useState({
    content: 0, testimonials: 0, contacts: 0, frameworks: 0
  });

  useEffect(() => {
    const token = localStorage.getItem("token");
    const headers = { Authorization: `Bearer ${token}` };
    
    Promise.all([
      axios.get(`${API}/content/all`, { headers }).then(r => r.data.length).catch(() => 0),
      axios.get(`${API}/testimonials`).then(r => r.data.length).catch(() => 0),
      axios.get(`${API}/contact`, { headers }).then(r => r.data.length).catch(() => 0),
      axios.get(`${API}/frameworks`, { headers }).then(r => r.data.length).catch(() => 0),
    ]).then(([content, testimonials, contacts, frameworks]) => {
      setStats({ content, testimonials, contacts, frameworks });
    });
  }, []);

  const cards = [
    { label: "Content Items", value: stats.content, icon: BookOpen, color: "bg-orange-500" },
    { label: "Testimonials", value: stats.testimonials, icon: Users, color: "bg-emerald-500" },
    { label: "Contact Requests", value: stats.contacts, icon: Mail, color: "bg-blue-500" },
    { label: "AI Frameworks", value: stats.frameworks, icon: Brain, color: "bg-purple-500" },
  ];

  return (
    <div data-testid="admin-dashboard-home">
      <h1 className="text-2xl font-serif font-medium text-slate-900 mb-6">Dashboard</h1>
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
        {cards.map((card) => (
          <Card key={card.label}>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500 mb-1">{card.label}</p>
                  <p className="text-3xl font-bold text-slate-900">{card.value}</p>
                </div>
                <div className={`w-12 h-12 ${card.color} rounded-xl flex items-center justify-center`}>
                  <card.icon className="h-6 w-6 text-white" />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

const ContentManager = () => {
  const [content, setContent] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({
    title: "", description: "", content_type: "article", 
    content_text: "", content_url: "", is_premium: false, 
    category: "", thumbnail_url: ""
  });
  const token = localStorage.getItem("token");

  const fetchContent = async () => {
    try {
      const res = await axios.get(`${API}/content/all`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setContent(res.data);
    } catch (err) {
      toast.error("Failed to fetch content");
    }
    setLoading(false);
  };

  useEffect(() => { fetchContent(); }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/content`, form, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Content created!");
      setForm({
        title: "", description: "", content_type: "article", 
        content_text: "", content_url: "", is_premium: false, 
        category: "", thumbnail_url: ""
      });
      fetchContent();
    } catch (err) {
      toast.error("Failed to create content");
    }
  };

  const handleDelete = async (id) => {
    if (!confirm("Delete this content?")) return;
    try {
      await axios.delete(`${API}/content/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Content deleted");
      fetchContent();
    } catch (err) {
      toast.error("Failed to delete");
    }
  };

  return (
    <div data-testid="admin-content-manager">
      <h1 className="text-2xl font-serif font-medium text-slate-900 mb-6">Content Manager</h1>
      
      <div className="grid lg:grid-cols-3 gap-8">
        <Card className="lg:col-span-1">
          <CardHeader>
            <CardTitle>Add Content</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <Input
                placeholder="Title"
                value={form.title}
                onChange={(e) => setForm({...form, title: e.target.value})}
                required
                data-testid="content-title"
              />
              <Textarea
                placeholder="Description"
                value={form.description}
                onChange={(e) => setForm({...form, description: e.target.value})}
                required
                data-testid="content-description"
              />
              <Select value={form.content_type} onValueChange={(v) => setForm({...form, content_type: v})}>
                <SelectTrigger data-testid="content-type-select">
                  <SelectValue placeholder="Content Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="article">Article</SelectItem>
                  <SelectItem value="video">Video</SelectItem>
                  <SelectItem value="pdf">PDF</SelectItem>
                </SelectContent>
              </Select>
              <Input
                placeholder="Category"
                value={form.category}
                onChange={(e) => setForm({...form, category: e.target.value})}
                required
                data-testid="content-category"
              />
              <Textarea
                placeholder="Content Text"
                value={form.content_text}
                onChange={(e) => setForm({...form, content_text: e.target.value})}
                data-testid="content-text"
              />
              <Input
                placeholder="Content URL (for videos/pdfs)"
                value={form.content_url}
                onChange={(e) => setForm({...form, content_url: e.target.value})}
                data-testid="content-url"
              />
              <div className="flex items-center justify-between">
                <span className="text-sm text-slate-600">Premium Content</span>
                <Switch
                  checked={form.is_premium}
                  onCheckedChange={(c) => setForm({...form, is_premium: c})}
                  data-testid="content-premium-toggle"
                />
              </div>
              <Button type="submit" className="w-full bg-orange-600 hover:bg-orange-500" data-testid="content-submit">
                <Plus className="h-4 w-4 mr-2" /> Add Content
              </Button>
            </form>
          </CardContent>
        </Card>

        <div className="lg:col-span-2">
          <Card>
            <CardHeader>
              <CardTitle>All Content ({content.length})</CardTitle>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[500px]">
                <div className="space-y-4">
                  {content.map((item) => (
                    <div key={item.id} className="flex items-start justify-between p-4 border rounded-lg" data-testid={`content-item-${item.id}`}>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-medium text-slate-900">{item.title}</h3>
                          {item.is_premium && <Badge className="bg-orange-600">Premium</Badge>}
                        </div>
                        <p className="text-sm text-slate-500 line-clamp-2">{item.description}</p>
                        <div className="flex items-center gap-2 mt-2">
                          <Badge variant="outline">{item.content_type}</Badge>
                          <Badge variant="secondary">{item.category}</Badge>
                        </div>
                      </div>
                      <Button variant="ghost" size="icon" onClick={() => handleDelete(item.id)} data-testid={`delete-content-${item.id}`}>
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

const TestimonialsManager = () => {
  const [testimonials, setTestimonials] = useState([]);
  const [form, setForm] = useState({ name: "", title: "", company: "", quote: "", image_url: "" });
  const [editingId, setEditingId] = useState(null);
  const [uploading, setUploading] = useState(false);
  const token = localStorage.getItem("token");

  const fetchTestimonials = async () => {
    const res = await axios.get(`${API}/testimonials`);
    setTestimonials(res.data);
  };

  useEffect(() => { fetchTestimonials(); }, []);

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await axios.post(`${API}/upload`, formData, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" }
      });
      const imageUrl = res.data.url;
      setForm(prev => ({ ...prev, image_url: imageUrl }));
      toast.success("Image uploaded!");
    } catch (err) {
      toast.error("Failed to upload image");
    }
    setUploading(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await axios.put(`${API}/testimonials/${editingId}`, form, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Testimonial updated!");
        setEditingId(null);
      } else {
        await axios.post(`${API}/testimonials`, form, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Testimonial added!");
      }
      setForm({ name: "", title: "", company: "", quote: "", image_url: "" });
      fetchTestimonials();
    } catch (err) {
      toast.error(editingId ? "Failed to update testimonial" : "Failed to add testimonial");
    }
  };

  const handleEdit = (t) => {
    setEditingId(t.id);
    setForm({ name: t.name, title: t.title, company: t.company, quote: t.quote, image_url: t.image_url || "" });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setForm({ name: "", title: "", company: "", quote: "", image_url: "" });
  };

  const handleMove = async (index, direction) => {
    const newList = [...testimonials];
    const swapIndex = direction === "up" ? index - 1 : index + 1;
    if (swapIndex < 0 || swapIndex >= newList.length) return;
    [newList[index], newList[swapIndex]] = [newList[swapIndex], newList[index]];
    setTestimonials(newList);
    try {
      await axios.put(`${API}/testimonials-reorder`, 
        { ids: newList.map(t => t.id) },
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch (err) {
      toast.error("Failed to save order");
      fetchTestimonials();
    }
  };

  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const handleDelete = async (id) => {
    if (deleteConfirm !== id) {
      setDeleteConfirm(id);
      return;
    }
    try {
      await axios.delete(`${API}/testimonials/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Testimonial deleted");
      setDeleteConfirm(null);
      fetchTestimonials();
    } catch (err) {
      toast.error("Failed to delete");
    }
  };

  return (
    <div data-testid="admin-testimonials-manager">
      <h1 className="text-2xl font-serif font-medium text-slate-900 mb-6">Testimonials</h1>
      
      <div className="grid lg:grid-cols-3 gap-8">
        <Card className="lg:col-span-1">
          <CardHeader>
            <CardTitle>{editingId ? "Edit Testimonial" : "Add Testimonial"}</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <Input placeholder="Name" value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} required data-testid="testimonial-name" />
              <Input placeholder="Title" value={form.title} onChange={(e) => setForm({...form, title: e.target.value})} required data-testid="testimonial-title" />
              <Input placeholder="Company" value={form.company} onChange={(e) => setForm({...form, company: e.target.value})} required data-testid="testimonial-company" />
              <Textarea placeholder="Quote" value={form.quote} onChange={(e) => setForm({...form, quote: e.target.value})} required data-testid="testimonial-quote" />
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Photo</label>
                {form.image_url ? (
                  <div className="flex items-center gap-3 mb-2">
                    <img src={resolveImageUrl(form.image_url)} alt="Preview" className="w-12 h-12 rounded-full object-cover" />
                    <button type="button" onClick={() => setForm({...form, image_url: ""})} className="text-sm text-red-500 hover:underline">Remove</button>
                  </div>
                ) : null}
                <label className="flex items-center justify-center w-full h-10 border-2 border-dashed border-stone-300 rounded-lg cursor-pointer hover:border-orange-400 transition-colors" data-testid="testimonial-upload">
                  <span className="text-sm text-slate-500">{uploading ? "Uploading..." : "Click to upload photo"}</span>
                  <input type="file" accept="image/*" onChange={handleImageUpload} className="hidden" disabled={uploading} />
                </label>
              </div>
              <Button type="submit" className="w-full bg-orange-600 hover:bg-orange-500" data-testid="testimonial-submit">
                <Plus className="h-4 w-4 mr-2" /> {editingId ? "Save Changes" : "Add Testimonial"}
              </Button>
              {editingId && (
                <Button type="button" variant="outline" className="w-full" onClick={handleCancelEdit} data-testid="testimonial-cancel-edit">
                  Cancel Edit
                </Button>
              )}
            </form>
          </CardContent>
        </Card>

        <div className="lg:col-span-2">
          <Card>
            <CardHeader><CardTitle>All Testimonials ({testimonials.length})</CardTitle></CardHeader>
            <CardContent>
              <div className="space-y-4">
                {testimonials.map((t, index) => (
                  <div key={t.id} className={`flex items-start gap-3 p-4 border rounded-lg ${editingId === t.id ? 'border-orange-400 bg-orange-50' : ''}`} data-testid={`testimonial-item-${t.id}`}>
                    {/* Order arrows */}
                    <div className="flex flex-col gap-1 pt-1">
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        className="h-7 w-7" 
                        disabled={index === 0}
                        onClick={() => handleMove(index, "up")}
                        data-testid={`move-up-${t.id}`}
                      >
                        <ArrowUp className="h-4 w-4 text-slate-400" />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        className="h-7 w-7"
                        disabled={index === testimonials.length - 1}
                        onClick={() => handleMove(index, "down")}
                        data-testid={`move-down-${t.id}`}
                      >
                        <ArrowDown className="h-4 w-4 text-slate-400" />
                      </Button>
                    </div>
                    {t.image_url && <img src={resolveImageUrl(t.image_url)} alt={t.name} className="w-12 h-12 rounded-full object-cover" />}
                    <div className="flex-1">
                      <h3 className="font-medium">{t.name}</h3>
                      <p className="text-sm text-slate-500">{t.title}, {t.company}</p>
                      <p className="text-sm text-slate-600 mt-2 line-clamp-2">"{t.quote}"</p>
                    </div>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="icon" onClick={() => handleEdit(t)} data-testid={`edit-testimonial-${t.id}`}>
                        <Pencil className="h-4 w-4 text-slate-500" />
                      </Button>
                      {deleteConfirm === t.id ? (
                        <div className="flex items-center gap-1">
                          <Button size="sm" variant="destructive" className="h-8 text-xs" onClick={() => handleDelete(t.id)} data-testid={`confirm-delete-${t.id}`}>
                            Confirm
                          </Button>
                          <Button size="sm" variant="ghost" className="h-8 text-xs" onClick={() => setDeleteConfirm(null)}>
                            Cancel
                          </Button>
                        </div>
                      ) : (
                        <Button variant="ghost" size="icon" onClick={() => handleDelete(t.id)} data-testid={`delete-testimonial-${t.id}`}>
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

const FrameworksManager = () => {
  const [frameworks, setFrameworks] = useState([]);
  const [form, setForm] = useState({ title: "", description: "", content: "", category: "" });
  const token = localStorage.getItem("token");

  const fetchFrameworks = async () => {
    try {
      const res = await axios.get(`${API}/frameworks`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFrameworks(res.data);
    } catch (err) {}
  };

  useEffect(() => { fetchFrameworks(); }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/frameworks`, form, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Framework added!");
      setForm({ title: "", description: "", content: "", category: "" });
      fetchFrameworks();
    } catch (err) {
      toast.error("Failed to add framework");
    }
  };

  const handleDelete = async (id) => {
    if (!confirm("Delete this framework?")) return;
    try {
      await axios.delete(`${API}/frameworks/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Framework deleted");
      fetchFrameworks();
    } catch (err) {
      toast.error("Failed to delete");
    }
  };

  return (
    <div data-testid="admin-frameworks-manager">
      <h1 className="text-2xl font-serif font-medium text-slate-900 mb-2">AI Coaching Frameworks</h1>
      <p className="text-slate-600 mb-6">
        Add coaching frameworks, methodologies, and knowledge to train the AI chatbot.
      </p>
      
      <div className="grid lg:grid-cols-3 gap-8">
        <Card className="lg:col-span-1">
          <CardHeader>
            <CardTitle>Add Framework</CardTitle>
            <CardDescription>Add coaching methodologies for the AI</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <Input placeholder="Framework Name" value={form.title} onChange={(e) => setForm({...form, title: e.target.value})} required data-testid="framework-title" />
              <Input placeholder="Brief Description" value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} required data-testid="framework-description" />
              <Input placeholder="Category (e.g., Coaching Models)" value={form.category} onChange={(e) => setForm({...form, category: e.target.value})} required data-testid="framework-category" />
              <Textarea 
                placeholder="Full framework content, instructions, and guidelines for the AI..."
                value={form.content} 
                onChange={(e) => setForm({...form, content: e.target.value})} 
                required 
                rows={8}
                data-testid="framework-content"
              />
              <Button type="submit" className="w-full bg-orange-600 hover:bg-orange-500" data-testid="framework-submit">
                <Plus className="h-4 w-4 mr-2" /> Add Framework
              </Button>
            </form>
          </CardContent>
        </Card>

        <div className="lg:col-span-2">
          <Card>
            <CardHeader><CardTitle>Training Frameworks ({frameworks.length})</CardTitle></CardHeader>
            <CardContent>
              <div className="space-y-4">
                {frameworks.map((f) => (
                  <div key={f.id} className="p-4 border rounded-lg" data-testid={`framework-item-${f.id}`}>
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <h3 className="font-medium text-slate-900">{f.title}</h3>
                        <Badge variant="secondary" className="mt-1">{f.category}</Badge>
                      </div>
                      <Button variant="ghost" size="icon" onClick={() => handleDelete(f.id)}>
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    </div>
                    <p className="text-sm text-slate-500 mb-2">{f.description}</p>
                    <p className="text-sm text-slate-600 whitespace-pre-wrap line-clamp-4">{f.content}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

const ContactsManager = () => {
  const [contacts, setContacts] = useState([]);
  const [filterStatus, setFilterStatus] = useState("all");
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const token = localStorage.getItem("token");

  const statuses = [
    { value: "new", label: "New", color: "bg-blue-500" },
    { value: "read", label: "Read", color: "bg-slate-400" },
    { value: "contacted", label: "Contacted", color: "bg-orange-500" },
    { value: "pending_coaching", label: "Pending Coaching", color: "bg-purple-500" },
    { value: "coaching", label: "Coaching", color: "bg-emerald-500" },
    { value: "past_coaching", label: "Past Coaching", color: "bg-stone-500" },
  ];

  const fetchContacts = () => {
    axios.get(`${API}/contact`, {
      headers: { Authorization: `Bearer ${token}` }
    }).then(res => setContacts(res.data)).catch(() => {});
  };

  useEffect(() => { fetchContacts(); }, []);

  const handleStatusChange = async (contactId, newStatus) => {
    try {
      await axios.put(`${API}/contact/${contactId}/status`, { status: newStatus }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setContacts(prev => prev.map(c => c.id === contactId ? { ...c, status: newStatus } : c));
      toast.success("Status updated");
    } catch (err) {
      toast.error("Failed to update status");
    }
  };

  const handleDelete = async (id) => {
    if (deleteConfirm !== id) {
      setDeleteConfirm(id);
      return;
    }
    try {
      await axios.delete(`${API}/contact/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Contact deleted");
      setDeleteConfirm(null);
      fetchContacts();
    } catch (err) {
      toast.error("Failed to delete");
    }
  };

  const filteredContacts = filterStatus === "all" 
    ? contacts 
    : contacts.filter(c => c.status === filterStatus);

  const getStatusInfo = (status) => statuses.find(s => s.value === status) || { label: status, color: "bg-slate-400" };

  return (
    <div data-testid="admin-contacts-manager">
      <h1 className="text-2xl font-serif font-medium text-slate-900 mb-2">Contact Submissions</h1>
      <p className="text-slate-600 mb-6">Manage inquiries from your website contact form.</p>

      {/* Filter bar */}
      <div className="flex flex-wrap gap-2 mb-6">
        <Button 
          size="sm" 
          variant={filterStatus === "all" ? "default" : "outline"}
          onClick={() => setFilterStatus("all")}
          data-testid="filter-all"
        >
          All ({contacts.length})
        </Button>
        {statuses.map(s => {
          const count = contacts.filter(c => c.status === s.value).length;
          return (
            <Button
              key={s.value}
              size="sm"
              variant={filterStatus === s.value ? "default" : "outline"}
              onClick={() => setFilterStatus(s.value)}
              data-testid={`filter-${s.value}`}
            >
              {s.label} ({count})
            </Button>
          );
        })}
      </div>
      
      <Card>
        <CardContent className="p-6">
          {filteredContacts.length === 0 ? (
            <p className="text-center text-slate-500 py-8">No contact submissions {filterStatus !== "all" ? `with status "${getStatusInfo(filterStatus).label}"` : "yet"}.</p>
          ) : (
            <div className="space-y-4">
              {filteredContacts.map((c) => {
                const statusInfo = getStatusInfo(c.status);
                return (
                  <div key={c.id} className="p-4 border rounded-lg" data-testid={`contact-item-${c.id}`}>
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h3 className="font-medium text-slate-900">{c.name}</h3>
                        <p className="text-sm text-slate-500">{c.email} {c.phone && `• ${c.phone}`}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <p className="text-xs text-slate-400">{new Date(c.created_at).toLocaleDateString()}</p>
                        {deleteConfirm === c.id ? (
                          <div className="flex items-center gap-1">
                            <Button size="sm" variant="destructive" className="h-7 text-xs" onClick={() => handleDelete(c.id)}>Delete</Button>
                            <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setDeleteConfirm(null)}>Cancel</Button>
                          </div>
                        ) : (
                          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => handleDelete(c.id)} data-testid={`delete-contact-${c.id}`}>
                            <Trash2 className="h-3.5 w-3.5 text-red-400" />
                          </Button>
                        )}
                      </div>
                    </div>
                    <p className="text-sm text-slate-600 mb-3">{c.message}</p>
                    {c.service_interest && <p className="text-xs text-slate-500 mb-3">Interested in: {c.service_interest}</p>}
                    <div className="flex flex-wrap gap-2">
                      {statuses.map(s => (
                        <button
                          key={s.value}
                          onClick={() => handleStatusChange(c.id, s.value)}
                          className={`px-3 py-1 rounded-full text-xs font-medium transition-all ${
                            c.status === s.value 
                              ? `${s.color} text-white` 
                              : "bg-stone-100 text-slate-500 hover:bg-stone-200"
                          }`}
                          data-testid={`status-${c.id}-${s.value}`}
                        >
                          {s.label}
                        </button>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

const PackagesManager = () => {
  const [packages, setPackages] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({
    name: "", tagline: "", description: "", price: 0,
    features: [""], image_url: "", is_featured: false
  });
  const [saving, setSaving] = useState(false);
  const token = localStorage.getItem("token");

  const fetchPackages = () => {
    axios.get(`${API}/packages`).then(res => setPackages(res.data)).catch(() => {});
  };

  useEffect(() => { fetchPackages(); }, []);

  const handleEdit = (p) => {
    setEditingId(p.id);
    setForm({
      name: p.name,
      tagline: p.tagline,
      description: p.description,
      price: p.price,
      features: p.features.length > 0 ? [...p.features] : [""],
      image_url: p.image_url || "",
      is_featured: p.is_featured
    });
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setForm({ name: "", tagline: "", description: "", price: 0, features: [""], image_url: "", is_featured: false });
  };

  const handleFeatureChange = (index, value) => {
    const updated = [...form.features];
    updated[index] = value;
    setForm({ ...form, features: updated });
  };

  const addFeature = () => {
    setForm({ ...form, features: [...form.features, ""] });
  };

  const removeFeature = (index) => {
    const updated = form.features.filter((_, i) => i !== index);
    setForm({ ...form, features: updated.length > 0 ? updated : [""] });
  };

  const handleSave = async () => {
    setSaving(true);
    const cleanFeatures = form.features.filter(f => f.trim() !== "");
    try {
      await axios.put(`${API}/packages/${editingId}`, {
        ...form,
        features: cleanFeatures
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Package updated!");
      setEditingId(null);
      fetchPackages();
    } catch (err) {
      toast.error("Failed to update package");
    }
    setSaving(false);
  };

  return (
    <div data-testid="admin-packages-manager">
      <h1 className="text-2xl font-serif font-medium text-slate-900 mb-6">Service Packages</h1>
      
      <div className="space-y-6">
        {packages.map((p) => (
          <Card key={p.id} className={editingId === p.id ? "border-orange-400" : ""} data-testid={`package-item-${p.id}`}>
            <CardContent className="p-6">
              {editingId === p.id ? (
                /* Edit Mode */
                <div className="space-y-4">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Package Name</label>
                      <Input value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} data-testid="edit-package-name" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Tagline</label>
                      <Input value={form.tagline} onChange={(e) => setForm({...form, tagline: e.target.value})} data-testid="edit-package-tagline" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Description</label>
                    <Textarea value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} rows={3} data-testid="edit-package-description" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Features / What's Included</label>
                    <div className="space-y-2">
                      {form.features.map((f, i) => (
                        <div key={i} className="flex gap-2">
                          <Input
                            value={f}
                            onChange={(e) => handleFeatureChange(i, e.target.value)}
                            placeholder={`Feature ${i + 1}`}
                            data-testid={`edit-feature-${i}`}
                          />
                          <Button type="button" variant="ghost" size="icon" onClick={() => removeFeature(i)} className="flex-shrink-0">
                            <Trash2 className="h-4 w-4 text-red-400" />
                          </Button>
                        </div>
                      ))}
                      <Button type="button" variant="outline" size="sm" onClick={addFeature} data-testid="add-feature-btn">
                        <Plus className="h-3 w-3 mr-1" /> Add Feature
                      </Button>
                    </div>
                  </div>
                  <div className="flex gap-3 pt-2">
                    <Button onClick={handleSave} disabled={saving} className="bg-orange-600 hover:bg-orange-500" data-testid="save-package-btn">
                      <Save className="h-4 w-4 mr-2" /> {saving ? "Saving..." : "Save Changes"}
                    </Button>
                    <Button variant="outline" onClick={handleCancelEdit}>Cancel</Button>
                  </div>
                </div>
              ) : (
                /* View Mode */
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-medium text-slate-900 text-lg">{p.name}</h3>
                      {p.is_featured && <Badge className="bg-orange-600">Featured</Badge>}
                    </div>
                    <p className="text-sm text-slate-500 mb-2">{p.tagline}</p>
                    <p className="text-sm text-slate-600 mb-3">{p.description}</p>
                    <ul className="space-y-1">
                      {p.features.map((f, i) => (
                        <li key={i} className="text-sm text-slate-600 flex items-center gap-2">
                          <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full flex-shrink-0" />
                          {f}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <Button variant="outline" size="sm" onClick={() => handleEdit(p)} data-testid={`edit-package-${p.id}`}>
                    <Pencil className="h-4 w-4 mr-2" /> Edit
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

const SiteContentEditor = () => {
  const [content, setContent] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const token = localStorage.getItem("token");

  useEffect(() => {
    axios.get(`${API}/site-content`).then(res => {
      setContent(res.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const handleChange = (key, value) => {
    setContent(prev => ({ ...prev, [key]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const items = Object.entries(content).map(([key, value]) => ({ key, value }));
      await axios.put(`${API}/site-content`, { items }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Site content saved! Changes are live.");
    } catch (err) {
      toast.error("Failed to save content");
    }
    setSaving(false);
  };

  const sections = [
    {
      title: "Hero Section",
      description: "The main banner at the top of your homepage",
      fields: [
        { key: "hero_overline", label: "Overline Text", type: "input" },
        { key: "hero_title_line1", label: "Title Line 1", type: "input" },
        { key: "hero_title_line2", label: "Title Line 2 (Bold)", type: "input" },
        { key: "hero_subtitle", label: "Subtitle / Description", type: "textarea" },
      ]
    },
    {
      title: "About Section",
      description: "Your bio, credentials, and background",
      fields: [
        { key: "about_overline", label: "Section Label", type: "input" },
        { key: "about_title", label: "Section Title", type: "input" },
        { key: "about_paragraph1", label: "Bio Paragraph 1", type: "textarea" },
        { key: "about_paragraph2", label: "Bio Paragraph 2", type: "textarea" },
        { key: "about_credential1_title", label: "Credential 1 - Title", type: "input" },
        { key: "about_credential1_subtitle", label: "Credential 1 - Subtitle", type: "input" },
        { key: "about_credential2_title", label: "Credential 2 - Title", type: "input" },
        { key: "about_credential2_subtitle", label: "Credential 2 - Subtitle", type: "input" },
        { key: "about_credential3_title", label: "Credential 3 - Title", type: "input" },
        { key: "about_credential3_subtitle", label: "Credential 3 - Subtitle", type: "input" },
        { key: "about_credential4_title", label: "Credential 4 - Title", type: "input" },
        { key: "about_credential4_subtitle", label: "Credential 4 - Subtitle", type: "input" },
        { key: "about_education_title", label: "Education Title", type: "input" },
        { key: "about_education_subtitle", label: "Education Subtitle", type: "input" },
      ]
    },
    {
      title: "Services Section",
      description: "Coaching packages header text",
      fields: [
        { key: "services_overline", label: "Section Label", type: "input" },
        { key: "services_title", label: "Section Title", type: "input" },
        { key: "services_subtitle", label: "Section Description", type: "textarea" },
      ]
    },
    {
      title: "Testimonials Section",
      description: "Success stories header text",
      fields: [
        { key: "testimonials_overline", label: "Section Label", type: "input" },
        { key: "testimonials_title", label: "Section Title", type: "input" },
      ]
    },
    {
      title: "Contact Section",
      description: "Contact area text and info",
      fields: [
        { key: "contact_overline", label: "Section Label", type: "input" },
        { key: "contact_title", label: "Section Title", type: "input" },
        { key: "contact_subtitle", label: "Section Description", type: "textarea" },
        { key: "contact_email", label: "Display Email", type: "input" },
      ]
    }
  ];

  if (loading) {
    return <div className="text-center py-12">Loading site content...</div>;
  }

  return (
    <div data-testid="admin-site-content-editor">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-serif font-medium text-slate-900">Edit Site Content</h1>
          <p className="text-slate-600">Edit the text on your website. Changes go live instantly when you save.</p>
        </div>
        <Button 
          onClick={handleSave} 
          disabled={saving}
          className="bg-orange-600 hover:bg-orange-500"
          data-testid="save-site-content"
        >
          <Save className="h-4 w-4 mr-2" />
          {saving ? "Saving..." : "Save All Changes"}
        </Button>
      </div>

      <div className="space-y-8">
        {sections.map((section) => (
          <Card key={section.title}>
            <CardHeader>
              <CardTitle className="text-lg">{section.title}</CardTitle>
              <CardDescription>{section.description}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {section.fields.map((field) => (
                <div key={field.key}>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">
                    {field.label}
                  </label>
                  {field.type === "textarea" ? (
                    <Textarea
                      value={content[field.key] || ""}
                      onChange={(e) => handleChange(field.key, e.target.value)}
                      rows={3}
                      className="resize-y"
                      data-testid={`cms-${field.key}`}
                    />
                  ) : (
                    <Input
                      value={content[field.key] || ""}
                      onChange={(e) => handleChange(field.key, e.target.value)}
                      data-testid={`cms-${field.key}`}
                    />
                  )}
                </div>
              ))}
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="mt-8 flex justify-end">
        <Button 
          onClick={handleSave} 
          disabled={saving}
          className="bg-orange-600 hover:bg-orange-500"
          data-testid="save-site-content-bottom"
        >
          <Save className="h-4 w-4 mr-2" />
          {saving ? "Saving..." : "Save All Changes"}
        </Button>
      </div>
    </div>
  );
};

const AdminDashboard = () => {
  return (
    <div className="flex min-h-screen bg-stone-50" data-testid="admin-dashboard">
      <AdminSidebar />
      <main className="flex-1 p-8">
        <Routes>
          <Route index element={<DashboardHome />} />
          <Route path="site-content" element={<SiteContentEditor />} />
          <Route path="content" element={<ContentManager />} />
          <Route path="testimonials" element={<TestimonialsManager />} />
          <Route path="frameworks" element={<FrameworksManager />} />
          <Route path="contacts" element={<ContactsManager />} />
          <Route path="packages" element={<PackagesManager />} />
        </Routes>
      </main>
    </div>
  );
};

export default AdminDashboard;
